// ═══════════════════════════════════════════════════════════════
// AiContentReportButton.jsx — Per-message flag button for reporting
// problematic AI output. Required for Google Play's 2024 generative
// AI policy. Submissions are written to data/reports/{reportId} in
// Firebase RTDB for later triage.
//
// Usage:
//   <AiContentReportButton
//     user={user}
//     content={message.content}
//     context={recentMessages}   // optional array of {role, content}
//     source="siyw-communication" // identifies the surface
//   />
//
// Renders as a subtle flag icon. Click opens a 1-screen modal with
// category selector + optional description. Submits and shows toast.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback, useId } from 'react';
import { Flag, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { storage, useToast } from '../shared.jsx';
import ModalDialog from './ModalDialog.jsx';

const CATEGORIES = [
  { value: 'harmful', label: 'Harmful or unsafe' },
  { value: 'inaccurate', label: 'Inaccurate / hallucinated' },
  { value: 'inappropriate', label: 'Inappropriate tone' },
  { value: 'other', label: 'Other' },
];

function AiContentReportButton({ user, content, context, source }) {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const [category, setCategory] = useState('harmful');
  const [description, setDescription] = useState('');
  const [phase, setPhase] = useState('input'); // input | submitting | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const toast = useToast();

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    setPhase('submitting');
    setErrorMsg('');
    try {
      const reportId = `rep-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      // Fall back to cached current-user if the parent didn't pass one
      let effectiveUser = user;
      if (!effectiveUser) {
        try {
          const raw = localStorage.getItem('argumentor-current-user');
          if (raw) effectiveUser = JSON.parse(raw);
        } catch (_) { /* ignore */ }
      }
      // Truncate to be safe (RTDB has size limits and reports shouldn't be huge)
      const safeContent = (content || '').toString().slice(0, 4000);
      const safeContext = Array.isArray(context)
        ? context.slice(-3).map((m) => ({
            role: (m?.role || 'unknown').toString().slice(0, 32),
            content: (m?.content || '').toString().slice(0, 2000),
          }))
        : null;
      const payload = {
        reportId,
        reporterUserId: effectiveUser?.id || null,
        reporterEmail: effectiveUser?.email || null,
        source: (source || 'unknown').toString().slice(0, 64),
        category,
        description: (description || '').toString().slice(0, 2000),
        content: safeContent,
        context: safeContext,
        at: new Date().toISOString(),
      };
      await storage.set(`reports/${reportId}`, payload, true);
      setPhase('done');
      toast('Thanks, your report has been recorded.');
      // Auto-close after a short delay
      setTimeout(() => {
        setOpen(false);
        setPhase('input');
        setDescription('');
        setCategory('harmful');
      }, 1200);
    } catch (e) {
      console.error('[AiContentReportButton] Submit failed:', e);
      setErrorMsg(e?.message || 'Could not submit report. Please try again.');
      setPhase('error');
    }
  }, [category, description, content, context, source, user, toast]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-stone-500 hover:text-amber-700 transition-colors p-1 -m-1 rounded"
        title="Report this AI response"
        aria-label="Report this AI response"
      >
        <Flag className="w-3.5 h-3.5" />
      </button>

      {open && (
        <ModalDialog onClose={phase !== 'submitting' ? () => setOpen(false) : undefined} labelledBy={titleId} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => phase !== 'submitting' && setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center">
                  {phase === 'done' ? <CheckCircle className="w-4 h-4 text-green-700" /> : <Flag className="w-4 h-4 text-amber-700" />}
                </div>
                <h2 id={titleId} className="text-base font-semibold text-stone-800">
                  {phase === 'done' ? 'Report Submitted' : 'Report AI Response'}
                </h2>
              </div>
              {phase !== 'submitting' && (
                <button onClick={() => setOpen(false)} className="text-stone-500 hover:text-stone-600" aria-label="Close">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {phase === 'input' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-xs text-stone-600">
                  Help us improve AI safety. Your report includes the AI response, surrounding context, and your account ID, used only for review by The ArguMentor team.
                </p>

                <div>
                  <label className="block text-xs font-medium text-stone-700 mb-1.5">What's the issue?</label>
                  <div className="space-y-1">
                    {CATEGORIES.map((c) => (
                      <label key={c.value} className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                        <input
                          type="radio"
                          name="report-category"
                          value={c.value}
                          checked={category === c.value}
                          onChange={() => setCategory(c.value)}
                          className="accent-amber-600"
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-700 mb-1.5">Description (optional)</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    placeholder="What was wrong with the AI response?"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Submit report
                  </button>
                </div>
              </form>
            )}

            {phase === 'submitting' && (
              <div className="py-6 flex flex-col items-center gap-2 text-stone-600">
                <Loader2 className="w-6 h-6 animate-spin text-amber-700" />
                <p className="text-sm">Submitting…</p>
              </div>
            )}

            {phase === 'done' && (
              <p className="text-sm text-stone-700">
                Thank you. Your report has been recorded and will be reviewed by The ArguMentor team.
              </p>
            )}

            {phase === 'error' && (
              <div className="space-y-3">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-800">{errorMsg}</p>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => setOpen(false)}
                    className="px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => setPhase('input')}
                    className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}
          </div>
        </ModalDialog>
      )}
    </>
  );
}

export default AiContentReportButton;
